/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { BrandColors, MaterialIcon, Mixins } from '@blend/enterprise-components';
import { DrawerInboxContainer } from '../../containers/DrawerInbox';
import { HEADER_HEIGHT } from '../../constants';
import { ReduxProps } from './DrawerContainer';
import { some } from 'lodash';
import { getFilteredThreads, threadHasANewMessage } from '../../../Inbox/utils/threadUtils';
import { BlendBridge } from 'blb-types/blendBridge';

export const DRAWER_WIDTH = 465;
export const DRAWER_BAR_WIDTH = 50;
export const TRANSITION = '0.5s ease';
const BORDER = `1px solid ${BrandColors.CLOUD_70}`;

const styles = StyleSheet.create({
  contentWrapper: {
    height: '100%',
    marginLeft: -1,
    overflowY: 'scroll',
    padding: '10px 0px',
    width: DRAWER_WIDTH - DRAWER_BAR_WIDTH,
  },
  drawerBar: {
    borderRight: BORDER,
    cursor: 'pointer',
    height: '100%',
    padding: '0 5px',
    width: DRAWER_BAR_WIDTH,
  },
  drawerOpen: {
    transform: 'translateX(0px)',
  },
  drawerWrapper: {
    background: BrandColors.WHITE,
    border: BORDER,
    bottom: 0,
    display: 'flex',
    height: `calc(100% - ${HEADER_HEIGHT}px)`,
    position: 'fixed',
    right: 0,
    transform: `translateX(${DRAWER_WIDTH - DRAWER_BAR_WIDTH}px)`,
    transition: TRANSITION,
    width: DRAWER_WIDTH,
    zIndex: 2,
  },
  iconWrapper: {
    borderBottom: BORDER,
    display: 'flex',
    justifyContent: 'center',
    padding: '25px 0',
    position: 'relative',
  },
  notificationIcon: {
    backgroundColor: BrandColors.SKY,
    position: 'absolute',
    right: 2,
    top: 20,
    ...Mixins.circle(6),
  },
});

export interface DrawerProps {
  drawerOpen: boolean;
  setDrawerOpen: (isOpen: boolean) => void;
  tenant: string;
  topic: BlendBridge.API.Topic;
  userId: string;
}

export type Props = DrawerProps & ReduxProps;
export const Drawer: React.FC<Props> = ({ userId, tenant, setDrawerOpen, drawerOpen, threads, topic }) => {
  const displayNotificationIcon = some(
    getFilteredThreads(threads, topic),
    thread => threadHasANewMessage(thread.lastRepliedBy.entityType) && !thread.isResolved,
  );

  return (
    <div className={css(styles.drawerWrapper, drawerOpen && styles.drawerOpen)}>
      <div className={css(styles.drawerBar)} id="it-DrawerBar" onClick={() => setDrawerOpen(!drawerOpen)}>
        <div className={css(styles.iconWrapper)}>
          {displayNotificationIcon && <div className={css(styles.notificationIcon)} id="it-NotificationIcon" />}
          <MaterialIcon color={BrandColors.NIGHT_90} size={20} type="mail_outline" />
        </div>
      </div>
      <div className={css(styles.contentWrapper)}>
        <DrawerInboxContainer tenant={tenant} topic={topic} userId={userId} />
      </div>
    </div>
  );
};
